// src/services/SupabaseAuthService.ts
/**
 * 🔐 Supabase Authentication Service
 * Gestisce login/register con backend Supabase
 */

import { supabase } from '../config/supabase';
import { User } from '@supabase/supabase-js';
import { MailNotificationService } from './MailNotificationService';

export class SupabaseAuthService {
  
  /**
   * Register new user with email/password and full name.
   * Non effettua auto-login: reindirizzare a /verify-email dopo la registrazione.
   */
  static async register(email: string, password: string, fullName: string = ''): Promise<{
    success: boolean;
    error?: string;
    user?: User;
    requiresEmailConfirmation?: boolean;
  }> {
    try {
      // Input validation
      if (!email || !password) {
        return { success: false, error: 'Email and password are required' };
      }

      if (password.length < 8) {
        return { success: false, error: 'La password deve contenere almeno 8 caratteri' };
      }

      if (!/\d/.test(password)) {
        return { success: false, error: 'La password deve contenere almeno un numero' };
      }

      // Register with Supabase — il trigger handle_new_user creerà il profilo
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
          data: { full_name: fullName }
        }
      });

      if (error) {
        console.error('❌ Registration error:', error.status, error.message);
        if (error.message === 'User already registered') {
          return { success: false, error: 'Esiste già un account con questa email. Accedi oppure recupera la password.' };
        }
        if (error.status === 429) {
          return { success: false, error: 'Troppi tentativi. Attendi qualche minuto e riprova.' };
        }
        if (error.message?.includes('redirect')) {
          return { success: false, error: 'Configurazione non valida. Contatta il supporto.' };
        }
        if (error.message?.includes('Signups not allowed')) {
          return { success: false, error: 'Le registrazioni sono temporaneamente disabilitate.' };
        }
        return { success: false, error: 'Registrazione non riuscita. Riprova.' };
      }

      if (!data.user) {
        return { success: false, error: 'Registrazione non riuscita. Riprova.' };
      }

      // Supabase non restituisce errore se l'email è già registrata (quando email confirmation è attiva):
      // ritorna invece un user con identities vuote. Intercettiamo questo caso.
      if (!data.user.identities || data.user.identities.length === 0) {
        return { success: false, error: 'Esiste già un account con questa email. Accedi oppure recupera la password.' };
      }

      console.log('✅ User registered successfully');

      // Welcome mail, deliberately not awaited. Registration has already
      // succeeded here, and a slow or failing mail provider must not turn a
      // good signup into an error the person sees. Sending at most once is
      // guaranteed server-side by the dedupe key, not by this call site.
      const mailLocale = (typeof navigator !== 'undefined' ? navigator.language || '' : '')
        .toLowerCase().startsWith('it') ? 'it' : 'en';
      void MailNotificationService.notifyUserRegistered(data.user.id, mailLocale);

      return {
        success: true,
        user: data.user,
        requiresEmailConfirmation: !data.session
      };

    } catch (error: any) {
      console.error('❌ Registration exception:', error);
      return {
        success: false,
        error: error.message || 'Registration failed'
      };
    }
  }

  /**
   * Login with email/password
   */
  static async login(email: string, password: string): Promise<{
    success: boolean;
    error?: string;
    user?: User;
  }> {
    try {
      // Input validation
      if (!email || !password) {
        return { success: false, error: 'Email and password are required' };
      }

      // Login with Supabase
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        console.error('❌ Login error');
        return { success: false, error: 'Email o password non corretti.' };
      }

      if (!data.user) {
        return { success: false, error: 'Login non riuscito. Riprova.' };
      }

      console.log('✅ User logged in successfully');
      
      return { 
        success: true, 
        user: data.user 
      };

    } catch (error: any) {
      console.error('❌ Login exception:', error);
      return { 
        success: false, 
        error: error.message || 'Login failed' 
      };
    }
  }

  /**
   * Logout current user
   */
  static async logout(): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase.auth.signOut();

      if (error) {
        console.error('❌ Logout error:', error);
        return { success: false, error: error.message };
      }

      // 🧹 CRITICAL: Clear ALL localStorage and sessionStorage
      // This prevents data leakage between different user accounts
      console.log('🧹 Clearing localStorage and sessionStorage...');
      localStorage.clear();
      sessionStorage.clear();

      console.log('✅ User logged out and cache cleared');
      return { success: true };

    } catch (error: any) {
      console.error('❌ Logout exception:', error);
      return {
        success: false,
        error: error.message || 'Logout failed'
      };
    }
  }

  /**
   * Get current logged in user
   */
  static async getCurrentUser(): Promise<User | null> {
    try {
      // Prefer the locally-persisted session: it is fast, offline-tolerant and
      // the SDK auto-refreshes an expiring token. getUser() hits the network on
      // every call and returns null on any transient blip — which was causing
      // intermittent "not authenticated" failures across the app.
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        return session.user;
      }

      // No local session — do one authoritative network check (also refreshes).
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error) {
        console.error('❌ Get user error:', error);
        return null;
      }
      return user;
    } catch (error) {
      console.error('❌ Get user exception:', error);
      return null;
    }
  }

  /**
   * Check if user is logged in.
   * Uses the persisted session (signed JWT, auto-refreshed) so a transient
   * network hiccup does not bounce an authenticated user to the landing page.
   * Real authorization is still enforced server-side by Supabase RLS.
   */
  static async isLoggedIn(): Promise<boolean> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) return true;
      const { data: { user }, error } = await supabase.auth.getUser();
      return !error && !!user;
    } catch (error) {
      console.error('❌ Session check exception:', error);
      return false;
    }
  }

  /**
   * Listen to auth state changes.
   * The callback receives the user and the raw Supabase event string so callers
   * can handle PASSWORD_RECOVERY differently from a normal login.
   */
  static onAuthStateChange(callback: (user: User | null, event: string) => void) {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('🔐 Auth state changed:', event);
      callback(session?.user ?? null, event);
    });

    // Return unsubscribe function
    return () => {
      subscription.unsubscribe();
    };
  }

  /**
   * Reset password (send email)
   */
  static async resetPassword(email: string): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/callback?type=recovery`,
      });

      if (error) {
        console.error('❌ Password reset error');
        return { success: false, error: 'Operazione non riuscita. Riprova.' };
      }

      return { success: true };

    } catch (error: any) {
      console.error('❌ Password reset exception');
      return {
        success: false,
        error: 'Operazione non riuscita. Riprova.'
      };
    }
  }

  /**
   * Get user display name from user_metadata.
   */
  static async getUserDisplayName(): Promise<string> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      return user?.user_metadata?.full_name || user?.email || '';
    } catch {
      return '';
    }
  }

  /**
   * Update password (when logged in)
   */
  /**
   * Prove the caller knows the password they are about to replace.
   *
   * Supabase will change a password from nothing but a valid session, so anyone
   * reaching a signed-in browser could take the account over — and changing the
   * password is exactly what a thief does first. Asking for the old one only
   * means something if it is checked against the server: a comparison in the
   * client proves nothing, because the client is the thing under the attacker's
   * control.
   *
   * A failed sign-in leaves the existing session alone; a successful one
   * refreshes it for the same user, which is harmless here.
   *
   * This closes the form. It does not close the API — whoever holds the session
   * can still call `updateUser({ password })` from the console. Only
   * `Authentication → Settings → Secure password change` in the Supabase
   * dashboard closes that, and it is still to be switched on.
   */
  static async verifyCurrentPassword(email: string, password: string): Promise<boolean> {
    if (!email || !password) return false;
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      return !error;
    } catch {
      return false;
    }
  }

  static async updatePassword(newPassword: string): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      if (newPassword.length < 8) {
        return { success: false, error: 'La password deve contenere almeno 8 caratteri' };
      }
      if (!/\d/.test(newPassword)) {
        return { success: false, error: 'La password deve contenere almeno un numero' };
      }

      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) {
        console.error('❌ Password update error');
        return { success: false, error: 'Aggiornamento password non riuscito. Riprova.' };
      }

      return { success: true };

    } catch (error: any) {
      console.error('❌ Password update exception');
      return {
        success: false,
        error: 'Aggiornamento password non riuscito. Riprova.'
      };
    }
  }
}
